import _ from 'lodash'
import store from '@ps/store'
import { addChildAt, setPaddingToNode, updateAsset } from '@ps/bridge/qc/node'
import { sortNodeByReverseTree } from '@ps/views/workbench/layer-manager/layer'
import setImage from './set-image'
import {
  getNativeSize,
  isConnectDrawLineNode,
  isGroup,
  isImage,
  isFreeDrawLineNode,
  isIvHotAreaNode,
} from '@ps/bridge/qc/base'
import * as nodeUtils from '../../common/node-utils'
import {
  executeAction,
  getAddAnimationAction,
  getAddGroupAction,
  getAddNodeAction,
  getClearActiveNodeAction,
  getCloneNodeAction,
  getLockNodeAction,
  getPasteNodeEventAction,
  getRemoveAnimationAction,
  getRemoveNodesAction,
  getSetAnimationAction,
  getSetStyleAction,
  getSwitchAnimationAction,
  getSwitchParentAction,
  getUpdateActiveNodeAction,
} from '@ps/workbench/undo-redo'
import dragTargetArea, {
  addDragBound,
  recoverDragTargetAreaByUuid,
  recoverDragBoundByUuid,
} from './drag-target-area'
import tweenPositionArea from './tween-position-area'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import {
  filter,
  findByUuid,
  resetNodeDefaultLayout,
  generateEmptyNode,
} from '@ps/bridge/qc/node/utils'
import gameIframe from '@ps/bridge/iframe/iframe'
import { createImageComponent } from '@ps/bridge/qc/bin/image'
import { updateImageComponent } from '@ps/workbench/resource-manager/update-image-in-playable'
import { addBuiltinAnimation, setAnimationName } from '@ps/workbench/animation'
import getResponsiveBox from '@com/utils/get-responsive-box'
import i18n from '@ps/lang'
import {
  getActionsByAnimation,
  getEventsToNode,
  getVpPlayTweenAnimationsFromNode,
} from '@ps/store/workbench/visual-program'
import * as bgScript from '@ps/bridge/qc/scripts/bg-config'
import * as animationScript from '@ps/bridge/qc/animation'
import { getFont } from '@ps/bridge/qc/node/text'
import { scripts } from '@ps/bridge/qc/scripts'
import { getVpAstFromSceneNode } from '@ps/bridge/qc/vp'
import { cloneNode } from '@ps/workbench/layer/node'
import { createImageComponentBeforeUpdateImage } from '@ps/workbench/layer/image'
import { getTextByFont } from '@ps/workbench/layer/text'
import { AssetRoute } from '@ps/utils/config'
import { hasBanRemoveNode } from '@ps/utils/cooperation'
import { language } from '@/common/storage'
import { nodeTypes } from '@ps/bridge/qc/scripts/editor'
import { DrawLineType } from '@ps/workbench/undo-redo/module/draw-line'
import { toQcColor } from '@ps/bridge/qc/color'
import {
  closePreviewAllBox2dShape,
  isRigidBody,
  refreshPreviewBox2dShape,
} from '@ps/workbench/undo-redo/module/box2d'
import {
  EraseType,
  getEraseCustomEvents,
} from '@ps/workbench/undo-redo/module/erase'
import { setNodeStyleMerge } from '@ps/bridge/qc/node/layout'
import {
  getIvSceneLinkNode,
  getEndHotAreaNode,
  isSceneToSceneLinkNode,
  wrapAddIvNode,
  getBrainItemByUuid,
} from '@ps/store/interactive-video/brain/brain'
import { isIvPage } from '@ps/router/config'
import nodePool from '@ps/bridge/qc/node-pool'
import { EventBus, Event } from '@ps/utils/event-bus'
import { findSceneSegment } from '@ps/store/interactive-video/timeline/utiles/scene'
import { getIvCurSceneStartFrame } from '@ps/store/interactive-video/timeline/timeline'

const isScene = (sceneList, node) =>
  !!node && !!sceneList.find((scene) => scene.uuid === node.uuid)

const getEventOnNode = (ast, node) => {
  const nodeAst = []
  for (const i in ast) {
    for (const j in ast[i].condition) {
      const condition = ast[i].condition[j]
      if (condition.node === node.uuid) {
        nodeAst.push(_.cloneDeep(ast[i]))
        break
      }
    }
  }
  return nodeAst
}

export default {
  /**
   * 更新当前节点列表
   * @param nodes 节点列表
   */
  updateActiveNodeList(
    { state, dispatch },
    { nodes, preActiveUuids, record = true },
  ) {
    if (_.isEmpty(nodes)) {
      state.activeNodeList = []
      dispatch('clearActiveNode', { record })
      return
    }

    // 使用最后的元素作为当前 activeNode
    const activeNode = nodes.pop()
    state.activeNodeList = nodes

    dispatch('updateActiveNode', {
      node: activeNode,
      multiple: true,
      preActiveUuids,
      record,
    })
  },
  /**
   * 更新当前节点
   * todo：抽离快捷键相关逻辑
   * @param node qc.node
   * @param multiple 是否是多选模式下的选择节点
   * @param preActiveUuids 多选的时候使用，多选拖动之前的当前节点uuid列表
   * @param record 是否需要记录操作记录，默认为 true
   * @returns {{activeNode: null | qc.node}}
   */
  updateActiveNode(
    { state, rootState, dispatch },
    { node, multiple = false, preActiveUuids, record = true },
  ) {
    const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
    if (!node || isScene(store.state.workspace.sceneManager.sceneList, node)) {
      return
    }
    if (isIvPage()) {
      if (
        node &&
        (isIvHotAreaNode(node) || isSceneToSceneLinkNode(node.uuid))
      ) {
        // 互动视频选中热区时，需要把时间线挪到热区对应的元件上，因为右面属性面板有热区数字，需要把热区显示在画布上
        const videoStart = getIvCurSceneStartFrame()
        let segment = findSceneSegment(currentSceneNode.uuid, node.uuid)
        let currentFrame = store.state.ivTimeline.currentFrame
        if (segment) {
          currentFrame = segment.start + videoStart
        } else {
          const linkNode = getIvSceneLinkNode(currentSceneNode.uuid)
          segment = findSceneSegment(currentSceneNode.uuid, linkNode.uuid)
          if (segment) {
            // 自动跳转没有互动元件segment，不用设置currentFrame
            currentFrame = segment.start + videoStart
          }
        }
        store.commit('ivTimeline/updateCurrentFrame', currentFrame)
      }
      if (node && isSceneToSceneLinkNode(node.uuid)) {
        // 互动视频项目选中场景间的互动元件，切换activeNode为实际的热区节点
        const uuids = getEndHotAreaNode(currentSceneNode.uuid)
        if (uuids.length > 0) {
          const brainBranchUuids = store.getters['ivBrain/brainBranchUuids']
          const index = brainBranchUuids.findIndex(
            (item) => item === currentSceneNode.uuid,
          )
          // 选择当前选中分支上的下一个场景节点对应的跳转热区
          if (index > -1 && brainBranchUuids[index + 1]) {
            const jumpSceneId = brainBranchUuids[index + 1]
            const brainItem = getBrainItemByUuid(currentSceneNode.uuid)
            const action = brainItem.actions.find(
              (item) => item.targetUuid === jumpSceneId,
            )
            node = nodePool.find(action.nodeUuid)
          } else {
            node = nodePool.find(uuids[0])
          }
        }
      }
      EventBus.$emit(Event.REFRESH_STAGE)
    }

    // 连续选中当前节点，只记录一次节点选中历史记录
    if (state.activeNode && node.uuid === state.activeNode.uuid) {
      record = false
    }

    const getPreUuid = () => {
      if (_.isEmpty(preActiveUuids)) {
        return state.activeNode ? state.activeNode.uuid : ''
      }
      return preActiveUuids[preActiveUuids.length - 1]
    }

    const curUuid = node.uuid
    const preUuids =
      preActiveUuids || state.activeNodeList.map((node) => node.uuid)
    const preUuid = getPreUuid()

    const getCurNodeUuids = () => {
      if (multiple) {
        // 如果节点id列表可以找到 node id，则认为是要取消该节点的选中状态
        return preUuids.some((item) => item === curUuid)
          ? preUuids.filter((item) => item !== curUuid)
          : [...preUuids, curUuid]
      }
      return [curUuid]
    }
    const curUuids = getCurNodeUuids()
    if (!isRigidBody(node) || isFreeDrawLineNode(node)) {
      store.commit('workspace/game/updatePreviewBox2dShapeState', {
        val: false,
      })
      store.commit('workspace/game/updatePreviewBox2dShapeAllState', {
        val: false,
      })
      closePreviewAllBox2dShape()
    }
    if (
      isRigidBody(node) &&
      !isFreeDrawLineNode(node) &&
      rootState.workspace.game.previewBox2dShapeState
    ) {
      refreshPreviewBox2dShape(node)
    }

    executeAction({
      action: getUpdateActiveNodeAction({
        oldValue: {
          uuids: preUuids, // 上一个选中的节点列表
          uuid: preUuid, // 上一个选中的节点id
        },
        newValue: {
          uuids: curUuids, // 当前选中的节点列表
          uuid: curUuid, // 当前选中的节点id
        },
      }),
      record,
    })
    // 更新节点，清理当前追踪埋点
    const sourcedSendAction =
      rootState.workspace.game.visualProgram.sourcedSendAction
    if (sourcedSendAction.node !== curUuid) {
      store.commit('workspace/game/visualProgram/updateSourcedSendAction', '')
    }

    return {
      activeNode: state.activeNode,
    }
  },

  clearActiveNode({ state, dispatch }, { record = true } = {}) {
    if (_.isEmpty(state.activeNodeList)) return
    const activeNode = state.activeNode
    const activeNodeList = state.activeNodeList

    // 更新节点，清理当前追踪埋点
    store.commit('workspace/game/visualProgram/updateSourcedSendAction', '')

    executeAction({
      action: getClearActiveNodeAction({
        oldValue: {
          uuid: activeNode ? activeNode.uuid : '',
          uuids: !_.isEmpty(activeNodeList)
            ? activeNodeList.map((node) => node.uuid)
            : null,
        },
      }),
      record,
    })
  },
  /**
   * 刷新激活节点，用于刷新节点的样式
   */
  async refreshActiveNode({ state, dispatch }, { record = true } = {}) {
    const activeNode = state.activeNode
    await dispatch('clearActiveNode', {
      record: false,
    })
    dispatch('updateActiveNode', {
      node: activeNode,
      record: false,
    })
  },

  /**
   * 设置节点样式
   * 如果是要设置文本和字体，则不要调用该 action，要调用 setFont 与 setText
   * @param nodes {{}[] | {}} 节点样式列表，格式是 [ { uuid: '123456', x:1, y:1, ...style}, { uuid: '123', x:1, y:1, ...style} ]
   * @param preNodes {{}[] | {}} 节点原来的样式列表，即要回滚的样式，格式是 [ { uuid: '123456', x:1, y:1, ...style}, { uuid: '123', x:1, y:1, ...style} ]
   * @param record {boolean} 是否加历史记录，默认是 true
   */
  setStyle({ state }, { nodes, preNodes, record = true }) {
    // 非数组的时候，默认将 nodes 当做单个节点属性
    if (!_.isArray(nodes)) {
      // 没有 uuid，补当前节点
      if (!nodes.uuid) nodes.uuid = state.activeNode.uuid
      nodes = [nodes]
    }

    if (nodes.some((node) => !node.uuid)) {
      throw new Error('节点缺少 uuid')
    }

    const uuids = nodes.map((item) => item.uuid)
    const targetQcNodes = filter(state.UIRoot, (node) =>
      uuids.some((item) => item === node.uuid),
    )
    if (preNodes) {
      if (!_.isArray(preNodes)) {
        preNodes = [preNodes]
      }
    }
    // 节点原始样式，即要回滚的样式
    const nodesOldStyle =
      preNodes || nodeUtils.getNodesOldStyle({ nodes, qcNodes: targetQcNodes })

    executeAction({
      action: getSetStyleAction({
        newValue: {
          nodes,
        },
        oldValue: {
          nodes: nodesOldStyle,
        },
      }),
      record,
    })
  },

  setAnimation({ state }, { animations, node, record = true }) {
    // 非数组的时候，默认将 nodes 当做单个节点属性
    if (!_.isArray(animations)) {
      animations = [animations]
    }

    if (animations.some((animation) => !animation.uuid)) {
      throw new Error('节点缺少 uuid')
    }

    if (!node) {
      // throw new Error('缺少动画父节点')
      return
    }

    const uuids = animations.map((item) => item.uuid)
    const scriptList = node.scripts.filter((script) =>
      uuids.some((item) => item === script.uuid),
    )

    // 节点原始样式，即要回滚的样式
    const nodesOldStyle = scriptList.map((script) => {
      return _.cloneDeep({
        ..._.pick(script, [
          'curve',
          'duration',
          'playOnAwake',
          'from',
          'to',
          'animationName',
          'delay',
          'style',
          'uuid',
        ]),
      })
    })

    // animationName 二次处理，如果是 json 字符串，只修改 json 字符串里面的 animationName
    animations.forEach((animation) => {
      if (animation.animationName) {
        const lang = language.get()
        const oldAnimation = nodesOldStyle.find(
          (script) => script.uuid === animation.uuid,
        )
        if (oldAnimation && oldAnimation.animationName) {
          const params = {
            animationName: oldAnimation.animationName,
          }
          if (lang === 'en') {
            params.name_en = animation.animationName
          } else {
            params.name = animation.animationName
          }
          animation.animationName = setAnimationName(params)
        } else {
          console.warn('没有找到对应的动画脚本')
        }
      }
    })

    executeAction({
      action: getSetAnimationAction({
        node,
        newValue: {
          animations,
        },
        oldValue: {
          animations: nodesOldStyle,
        },
      }),
      record,
    })
  },

  switchAnimation({ state }, { animation, newAnimation, node, record = true }) {
    if (!node) {
      // throw new Error('缺少动画父节点')
    }

    // 添加预设逻辑
    const components = addBuiltinAnimation({
      node,
      animations: newAnimation.id ? newAnimation.builtin : newAnimation,
    })

    // 赋值动画名称
    // components.forEach(item => {
    //   item.animationName = animation.animationName
    // })

    // 先移除动画脚本，根据原动画位置重新插入
    _.pullAll(node.scripts, components)

    // 移除目标动画
    const getReplaceIndex = (animation) => {
      if (animation.builtinId) {
        return node.scripts.findIndex(
          (item) => item.uuid === animation.group[0].uuid,
        )
      }
      return node.scripts.findIndex((item) => item.uuid === animation.uuid)
    }

    const replaceIndex = getReplaceIndex(animation)
    let removeList
    if (animation.builtinId) {
      removeList = node.scripts.filter(
        (item) =>
          animation.group.map((el) => el.uuid).indexOf(item.uuid) !== -1,
      )
    } else {
      removeList = [animation]
    }
    executeAction({
      action: getSwitchAnimationAction({
        node,
        newValue: {
          animations: removeList,
          newAnimations: components,
          index: replaceIndex,
        },
        oldValue: {
          animations: removeList,
          newAnimations: components,
          index: replaceIndex,
        },
      }),
      record,
    })

    return components
  },

  async addEmptyNode({ state }, scene) {
    const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
    const gameOb = state.data.gameOb
    const node = generateEmptyNode(currentSceneNode, gameOb)
    const index = currentSceneNode.getChildIndex(node)
    // 互动视频编辑器插入节点处理
    const ivRes = await wrapAddIvNode(currentSceneNode, node, index)

    executeAction({
      action: getAddNodeAction({
        newValue: {
          node: ivRes ? ivRes.ivGroupNode : node,
          realNode: node,
          parent: currentSceneNode,
          index: ivRes ? ivRes.index : index,
        },
        oldValue: {
          node: ivRes ? ivRes.ivGroupNode : node,
          realNode: node,
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
        },
      }),
    })
  },

  addDrawLineNode({ state }) {
    const gameOb = state.data.gameOb
    const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
    const node = gameOb.add.image(currentSceneNode)
    const editorData = editorScript.getScript(node)
    editorData.$data.nodeType = nodeTypes.drawLine
    editorData.$data.createFromPSScripts = [scripts.drawLine.name]
    node.name = i18n.t('workbench.drawLine.drawLineComponent', 'en')
    node.width = gameIframe.UIRoot.width
    node.height = gameIframe.UIRoot.height
    node.addScript(scripts.drawLine.name)
    // 新增的连线组件,默认自带两个起止区域
    if (isConnectDrawLineNode(node)) {
      const gameOb = store.state.workspace.game.data.gameOb
      const node1 = gameOb.add.node(node)
      node1.name = '1'
      node1.width = 200
      node1.height = 200
      node1.x = node1.width / 2 - node.width / 2
      node1.y = node1.height / 2 - node.height / 2
      const editorData1 = editorScript.getScript(node1)
      editorData1.$data.nodeType = nodeTypes.drawLineArea
      editorData1.$data.createFromPSScripts = [scripts.drawLineArea.name]
      node1.addScript(scripts.drawLineArea.name)
      // 初始化layout
      store.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
        node: node1,
      })
      store.dispatch('workspace/game/drawLine/addNodeForPairsData', node1)
      const node2 = gameOb.add.node(node)
      node2.name = '2'
      node2.width = 200
      node2.height = 200
      node2.x = node.width / 2 - node2.width / 2
      node2.y = node.height / 2 - node2.height / 2
      const editorData2 = editorScript.getScript(node2)
      editorData2.$data.nodeType = nodeTypes.drawLineArea
      editorData2.$data.createFromPSScripts = [scripts.drawLineArea.name]
      node2.addScript(scripts.drawLineArea.name)
      // 初始化layout
      store.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
        node: node2,
      })
      store.dispatch('workspace/game/drawLine/addNodeForPairsData', node2)
    }
    state.firstComponentTip = true
    executeAction({
      action: getAddNodeAction({
        newValue: {
          node,
          parent: currentSceneNode,
          index: currentSceneNode.getChildIndex(node),
        },
        oldValue: {
          node: node,
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
        },
      }),
    })
  },

  addDrawLineAreaNode({ state }, parentNode) {
    const gameOb = state.data.gameOb
    const pNode = parentNode
    let name = ''
    if (pNode.children.length > 0) {
      const maxNumNode = pNode.children[pNode.children.length - 1]
      name = String(Number(maxNumNode.name) + 1)
    } else {
      name = '1'
    }
    const node = gameOb.add.node(pNode)
    const editorData = editorScript.getScript(node)
    editorData.$data.nodeType = nodeTypes.drawLineArea
    editorData.$data.createFromPSScripts = [scripts.drawLineArea.name]
    node.name = name
    // 连线组件的实际宽高小200，添加的起止区域的宽高为连线组件的宽高
    node.width = pNode.width * pNode.scaleX > 200 ? 200 : pNode.width
    node.height = pNode.height * pNode.scaleY > 200 ? 200 : pNode.height
    node.addScript(scripts.drawLineArea.name)

    executeAction({
      action: getAddNodeAction({
        newValue: {
          node,
          parent: pNode,
          index: pNode.getChildIndex(node),
        },
        oldValue: {
          node: node,
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
        },
      }),
    })
  },

  addFreeDrawLineNode({ state }) {
    const gameOb = state.data.gameOb
    const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
    const node = gameOb.add.image(currentSceneNode)
    const editorData = editorScript.getScript(node)
    editorData.$data.nodeType = nodeTypes.drawLine
    editorData.$data.createFromPSScripts = [scripts.drawLine.name]
    node.name = i18n.t('workbench.drawLine.freeDrawLineComponent', 'en')
    node.width = gameIframe.UIRoot.width
    node.height = gameIframe.UIRoot.height
    const drawLineScript = node.addScript(scripts.drawLine.name)
    drawLineScript.drawLineType = DrawLineType.FREE_LINE
    drawLineScript.brushColor = toQcColor('rgba(0, 0, 0, 1)')
    state.firstComponentTip = true
    executeAction({
      action: getAddNodeAction({
        newValue: {
          node,
          parent: currentSceneNode,
          index: currentSceneNode.getChildIndex(node),
        },
        oldValue: {
          node: node,
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
        },
      }),
    })
  },

  addEraseNode({ state }) {
    const gameOb = state.data.gameOb
    const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
    const node = gameOb.add.node(currentSceneNode)
    node.name = i18n.t('workbench.erase.eraseComponent', 'en')
    node.width = gameIframe.UIRoot.width
    node.height = gameIframe.UIRoot.height
    // 底图图层组
    const bottomImages = gameOb.add.node(node)
    bottomImages.name = i18n.t('workbench.erase.layer.bottomImage', 'en')
    // 蒙层图层组
    const maskImages = gameOb.add.node(node)
    maskImages.name = i18n.t('workbench.erase.layer.maskLayer', 'en')
    // 自定义判定区域组
    const customJudgeAreas = gameOb.add.node(node)
    customJudgeAreas.name = 'customJudgeAreas'
    // 跟手图片组
    const brushImages = gameOb.add.node(node)
    brushImages.name = 'brushImages'
    // 笔触样式组
    const brushCategory = gameOb.add.node(node)
    brushCategory.name = 'brushCategory'
    const round = gameOb.add.image(brushCategory)
    round.name = 'round'
    round.width = 100
    round.height = 100
    round.alpha = 0.7
    const ped = editorScript.getScript(round)
    ped.$data.ratioLock = true
    round.visible = false
    const rect = gameOb.add.image(brushCategory)
    rect.name = 'rect'
    rect.width = 100
    rect.height = 100
    rect.alpha = 0.7
    rect.visible = false
    // 挂载脚本与绑定节点
    const eraseComponent = node.addScript(scripts.erase.name)
    const editorData = editorScript.getScript(node)
    editorData.$data.nodeType = nodeTypes.erase
    editorData.$data.createFromPSScripts = [scripts.erase.name]
    eraseComponent.brushCategory = brushCategory
    eraseComponent.brushImages = brushImages
    eraseComponent.bottomImages = bottomImages
    eraseComponent.maskImages = maskImages
    eraseComponent.customJudgeAreas = customJudgeAreas
    // 蒙层脚本参数设置
    const maskEraseGroup = maskImages.addScript(scripts.eraseGroup.name)
    const maskEditorData = editorScript.getScript(maskImages)
    maskEditorData.$data.createFromPSScripts = [scripts.eraseGroup.name]
    maskEditorData.$data.nodeType = nodeTypes.group
    maskEraseGroup.canErase = true
    maskEraseGroup.eraseGroupType = EraseType.MASK
    maskEraseGroup.isEnableEraseable = true
    // 底图脚本参数设置
    const bottomEditorData = editorScript.getScript(bottomImages)
    bottomEditorData.$data.nodeType = nodeTypes.group
    bottomEditorData.$data.createFromPSScripts = [scripts.eraseGroup.name]
    const bottomImagesEraseGroup = bottomImages.addScript(
      scripts.eraseGroup.name,
    )
    bottomImagesEraseGroup.canErase = false
    bottomImagesEraseGroup.eraseGroupType = EraseType.BOTTOM_IMAGE
    bottomImagesEraseGroup.isEnableEraseable = false
    // 自定义判定区域脚本参数设置
    const areaEraseGroup = customJudgeAreas.addScript(scripts.eraseGroup.name)
    const areaEditorData = editorScript.getScript(customJudgeAreas)
    areaEditorData.$data.createFromPSScripts = [scripts.eraseGroup.name]
    areaEraseGroup.canErase = true
    areaEraseGroup.eraseGroupType = EraseType.CUSTOM_JUDGE_AREA
    areaEraseGroup.isEnableEraseable = true

    state.firstComponentTip = true
    executeAction({
      action: getAddNodeAction({
        newValue: {
          node,
          parent: currentSceneNode,
          index: currentSceneNode.getChildIndex(node),
        },
        oldValue: {
          node: node,
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
        },
      }),
    })
  },

  addMaskNode({ state }) {
    const gameOb = state.data.gameOb
    const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
    const isBlankProject = store.state.workspace.project.isBlankProject
    const node = gameOb.add.node(currentSceneNode)
    node.name = i18n.t('workbench.mask.maskComponent', 'en')
    const min = Math.min(gameIframe.UIRoot.width, gameIframe.UIRoot.height)
    node.width = min
    node.height = min
    // 挂载脚本与绑定节点
    let editorData = editorScript.getScript(node)
    editorData.$data.nodeType = nodeTypes.mask
    const maskComponent = node.addScript(scripts.mask.name)
    maskComponent.isEnable = true
    editorData.$data.createFromPSScripts = [scripts.mask.name]
    // 遮罩组
    const maskGroup = gameOb.add.node(node)
    maskGroup.name = 'Group 1'
    editorData = editorScript.getScript(maskGroup)
    editorData.$data.nodeType = nodeTypes.group
    const maskGroupComponent = maskGroup.addScript(scripts.maskGroup.name)
    editorData.$data.createFromPSScripts = [scripts.maskGroup.name]
    if (isBlankProject) {
      maskGroupComponent.isCreatedByBlankProject = true
    } else {
      maskGroupComponent.isCreatedByBlankProject = false
    }
    maskGroupComponent.isEnable = true
    // 被遮罩组
    const maskedGroup = gameOb.add.node(maskGroup)
    maskedGroup.name = 'maskedGroup'
    editorData = editorScript.getScript(maskedGroup)
    editorData.$data.nodeType = nodeTypes.group
    maskGroupComponent.maskGroupLayer = maskedGroup
    // 遮罩形状
    const maskShapeGroup = gameOb.add.node(maskGroup)
    maskShapeGroup.name = 'maskShapeGroup'
    editorData = editorScript.getScript(maskShapeGroup)
    editorData.$data.nodeType = nodeTypes.group
    maskGroupComponent.maskShapeLayer = maskShapeGroup
    // 形状组
    const maskShape = gameOb.add.node(maskShapeGroup)
    maskShape.name = 'maskShape'
    setNodeStyleMerge(maskShape, {
      width: 200,
      height: 200,
    })
    editorData = editorScript.getScript(maskShape)
    editorData.$data.nodeType = nodeTypes.group
    maskGroupComponent.maskShape = maskShape
    store.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node: maskShape,
    })
    // 编辑器辅助形状节点
    const aidShapeNode = gameOb.add.image(maskShape)
    aidShapeNode.name = 'aidShape'
    setNodeStyleMerge(aidShapeNode, {
      width: maskShape.width,
      height: maskShape.height,
    })
    aidShapeNode.visible = true
    // 跟随元素组
    const followGroup = gameOb.add.node(maskShapeGroup)
    followGroup.name = 'followGroup'
    editorData = editorScript.getScript(followGroup)
    editorData.$data.nodeType = nodeTypes.group
    maskGroupComponent.followElement = followGroup
    store.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
      node: followGroup,
    })

    state.firstComponentTip = true
    executeAction({
      action: getAddNodeAction({
        newValue: {
          node,
          parent: currentSceneNode,
          index: currentSceneNode.getChildIndex(node),
        },
        oldValue: {
          node: node,
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
        },
      }),
    })
  },
  addAnimation({ state }, { animations, nodes, record = true }) {
    if (!_.isArray(animations)) {
      animations = [animations]
    }

    const activeNode = state.activeNode

    // 当前节点动画脚本列表
    let activeNodeAnimationScripts = []

    // 节点动画脚本列表
    const animationScripts = animationScript.getAnimationScripts({
      targetNodes: nodes,
      animations,
    })

    if (animationScripts && !_.isEmpty(animationScripts)) {
      activeNodeAnimationScripts = animationScripts[activeNode.uuid]
      executeAction({
        action: getAddAnimationAction({
          newValue: {
            nodes,
            animationScripts: animationScripts,
          },
          oldValue: {
            nodes,
            animationScripts: animationScripts,
          },
        }),
        record,
      })
    }

    return activeNodeAnimationScripts
  },

  removeAnimation(
    { state, rootState },
    { animation, node, record = true } = {},
  ) {
    if (!animation.uuid) {
      throw new Error('节点缺少 uuid')
    }

    if (!node) {
      // throw new Error('缺少动画父节点')
      return
    }
    let scriptList
    if (animation.builtinId) {
      scriptList = node.scripts.filter(
        (item) =>
          animation.group.map((el) => el.uuid).indexOf(item.uuid) !== -1,
      )
    } else {
      scriptList = node.scripts.filter((item) => item.uuid === animation.uuid)
    }

    // 获取移除动画排序位置
    const getReplaceIndex = (animation) => {
      if (animation.builtinId) {
        return node.scripts.findIndex(
          (item) => item.uuid === animation.group[0].uuid,
        )
      }
      return node.scripts.findIndex((item) => item.uuid === animation.uuid)
    }

    const actionsWithAnimation = getActionsByAnimation({
      animationId: animation.uuid,
    })
    const actionsWithVpPlayTweenAnimation =
      getVpPlayTweenAnimationsFromNode(node)

    executeAction({
      action: getRemoveAnimationAction({
        node,
        newValue: {
          animations: scriptList,
          actionsWithAnimation,
          actionsWithVpPlayTweenAnimation,
        },
        oldValue: {
          index: getReplaceIndex(animation),
          animations: scriptList,
        },
      }),
      record,
    })
  },

  /**
   * removeNode
   * @param {{}[] | {}} nodes:qc.Node
   */
  removeNode(
    { state, dispatch, rootState },
    { nodes, poolItem, record = true } = {},
  ) {
    if (!nodes) return

    if (!_.isArray(nodes)) {
      nodes = [nodes]
    }
    // 协同流程中,需要判断该节点是否是研发声明禁止删除的
    for (const i in nodes) {
      if (hasBanRemoveNode(nodes[i])) return
    }

    const originalNodes = filter(state.UIRoot, (item) =>
      nodes.some((j) => j.uuid === item.uuid),
    )
    const eventsToNode = { condition: [], action: [] }
    originalNodes.forEach((node) => {
      const { condition, action } = getEventsToNode({ nodeId: node.uuid })
      eventsToNode.condition.push(...condition)
      eventsToNode.action.push(...action)
    })
    const cloneAst = _.cloneDeep(rootState.workspace.game.visualProgram.ast)
    const clonePairsData = _.cloneDeep(
      rootState.workspace.game.drawLine.pairsData,
    )
    const cloneAreasData = _.cloneDeep(
      rootState.workspace.game.drawLine.areasData,
    )
    const cloneEraseCustomEvents = _.cloneDeep(getEraseCustomEvents(nodes[0]))
    executeAction({
      action: getRemoveNodesAction({
        newValue: {
          nodes: originalNodes,
          activeUuids: state.activeNodeList.map((item) => item.uuid),
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
          poolItem,
        },
        oldValue: {
          nodes: originalNodes,
          // 子节点被删除后，再通过被删除的节点去获取 parent，返回 null
          parents: originalNodes.map((item) => item.parent),
          // 子节点被删除后，再通过父节点无法再获取到，所以需要单独加索引列表
          indexs: originalNodes.map((item) => item.parent.getChildIndex(item)),
          activeUuids: state.activeNodeList.map((item) => item.uuid),
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
          eventsToNode,
          ast: cloneAst,
          pairsData: clonePairsData,
          areasData: cloneAreasData,
          eraseCustomEvents: cloneEraseCustomEvents,
          poolItem,
        },
      }),
      record,
    })
  },

  lockNode({ state }, { nodes, record = true } = {}) {
    if (!nodes) return

    executeAction({
      action: getLockNodeAction({
        oldValue: {
          uuids: _.cloneDeep(state.lockedNodeUuids),
        },
        newValue: {
          uuids: nodes.map((item) => item.uuid),
        },
      }),
      record,
    })
  },

  /**
   * @param {qc.Node[]} copyNodes 要复制的节点
   * @param {qc.Node} targetNode 目标节点
   * @param {number} index 复制到目标节点后代的索引位置
   */
  async pasteNode(
    { state, rootState, commit },
    { copyNodes, targetNode, index, record = true },
  ) {
    if (!copyNodes || !targetNode) return

    if (!_.isArray(copyNodes)) {
      copyNodes = [copyNodes]
    }

    copyNodes = sortNodeByReverseTree(
      copyNodes,
      _.get(
        rootState,
        'workspace.game.sceneManager.currentSceneNode.children',
      ) || [],
    )

    if (copyNodes.some((node) => node.parent.name === 'UIRoot')) {
      return
    }

    const newNodes = cloneNode({
      originalNodes: copyNodes,
      index,
      parent: targetNode,
    })

    const currentSceneEditType =
      rootState.workspace.game.sceneManager.sceneEditType

    executeAction({
      action: getCloneNodeAction({
        newValue: {
          originalNodes: copyNodes,
          nodes: newNodes,
          parent: targetNode,
          index,
          currentSceneEditType,
        },
        oldValue: {
          nodes: newNodes,
          activeUuid: state.activeNode ? state.activeNode.uuid : '',
        },
      }),
      record,
    })
  },

  /**
   * 新增组
   */
  addGroup(
    { rootState, state },
    { nodes, targetNode, index, name, record = true },
  ) {
    const sourceNode = findByUuid(targetNode.uuid)
    const groupNode = gameIframe.qcGame.add.node()
    resetNodeDefaultLayout(groupNode)
    groupNode.name = name || 'group'
    const editorData = editorScript.getScript(groupNode)
    editorData.$data.nodeType = 'group'
    sourceNode.addChildAt(groupNode, index)
    executeAction({
      action: getAddGroupAction({
        newValue: {
          group: groupNode,
          nodes: nodes,
          parent: sourceNode,
          index: sourceNode.getChildIndex(groupNode),
        },
        oldValue: {
          group: groupNode,
          nodes: nodes,
          parents: nodes.map((item) => item.parent),
          indexs: nodes.map((item) => item.parent.getChildIndex(item)),
        },
      }),
      record,
    })

    return groupNode
  },

  /**
   * 解除组
   * @param {*} param0
   * @param {*} param1
   */
  async ungroup({ rootState, state, dispatch }, { groups, record = true }) {
    if (!_.isArray(groups)) {
      groups = [groups]
    }
    const selectedGroups = groups
    // 过滤非组节点元素
    groups = groups
      .filter((group) => isGroup(group))
      .map((group) => findByUuid(group.uuid))

    executeAction({
      action: {
        execute: ({ newValue }) => {
          const { groups } = newValue
          groups.forEach((group) => {
            const groupParent = group.parent
            const position = groupParent.getChildIndex(group)
            // 子节点转移到父级同位置
            nodeUtils.switchParentAndRefresh({
              nodes: [...group.children.reverse()],
              parent: groupParent,
              index: position,
            })
            // 移除组
            store.dispatch('workspace/game/removeNode', {
              nodes: [group],
              record: false,
            })
          })
          state.activeNodeList = []
          state.activeNode = null
        },
        undo: ({ oldValue }) => {
          const { groups, parents, indexs, childrens, selectedGroups } =
            oldValue
          groups.forEach((group, groupIndex) => {
            const parent = parents[groupIndex]
            const index = indexs[groupIndex]
            const children = childrens[groupIndex]
            addChildAt({
              node: group,
              parent,
              index,
            })
            children.reverse().forEach((child, childIndex) => {
              nodeUtils.switchParentAndRefresh({
                nodes: [child],
                parent: group,
                index: childIndex,
              })
            })
          })
          state.activeNodeList = selectedGroups
          state.activeNode = selectedGroups[0]
        },
        value: {
          newValue: {
            groups,
          },
          oldValue: {
            groups,
            parents: groups.map((group) => group.parent),
            indexs: groups.map((group) => group.parent.getChildIndex(group)),
            childrens: groups.map((group) => group.children),
            selectedGroups,
          },
        },
        methodName: 'unGroupAction',
      },
      record,
    })
  },

  /**
   * @param {any} node 待调整父节点的节点
   * @param {any} targetNode 目标节点
   * @param {number} targetIndex 目标位置
   */
  async switchParent(
    { rootState, state, commit },
    { nodes, targetNode, targetIndex, record = true },
  ) {
    if (!_.isArray(nodes)) {
      nodes = [nodes]
    }

    executeAction({
      action: getSwitchParentAction({
        newValue: {
          nodes,
          parent: targetNode,
          index: targetIndex,
        },
        oldValue: {
          nodes,
          parents: nodes.map((item) => item.parent),
          indexs: nodes.map((item) => item.parent.getChildIndex(item)),
        },
      }),
      record,
    })
  },

  layerSwitchParent(
    { rootState, state, commit },
    { nodes, targetNode, targetIndex, sort },
  ) {
    if (!_.isArray(nodes)) {
      nodes = [nodes]
    }
    const qcTarget = findByUuid(targetNode.uuid)
    // 获取源节点
    nodes = nodes.map((node) => findByUuid(node.uuid))
    // 根据源树节点进行排序
    if (sort) {
      nodes = sortNodeByReverseTree(
        nodes,
        _.get(
          rootState,
          'workspace.game.sceneManager.currentSceneNode.children',
        ) || [],
      )
    }
    store.dispatch('workspace/game/switchParent', {
      nodes: nodes,
      targetNode: qcTarget,
      targetIndex,
    })
  },

  /**
   * 上移动一层
   * @returns
   */
  switchNodeUp({ rootState, state, commit }, { nodes }) {
    if (!nodes) return

    if (!_.isArray(nodes)) {
      nodes = [nodes]
    }

    // 按照树正序排列
    nodes = sortNodeByReverseTree(
      nodes,
      _.get(
        rootState,
        'workspace.game.sceneManager.currentSceneNode.children',
      ) || [],
    )
    // 当前场景节点
    const currentSceneNode = _.get(state, 'sceneManager.currentSceneNode')

    // 获取第一个节点
    const firstNode = nodes[0]

    const pickPosition = (node) => {
      const index = node.parent.getChildIndex(node)
      // 到达根节点顶部
      if (
        index === node.parent.children.length - 1 &&
        node.parent.uuid === currentSceneNode.uuid
      ) {
        return {
          target: node.parent,
          index: node.parent.children.length - 1,
        }
      } else if (
        index === node.parent.children.length - 1 &&
        node.parent.uuid !== currentSceneNode.uuid
      ) {
        return {
          target: node.parent.parent,
          index: node.parent.parent.getChildIndex(node.parent) + 1,
        }
      }
      return {
        target: node.parent,
        index: index + 2,
      }
    }
    const position = pickPosition(firstNode)
    store.dispatch('workspace/game/switchParent', {
      nodes: nodes,
      targetNode: position.target,
      targetIndex: position.index,
    })
  },

  /**
   * 上移到顶部
   * @returns
   */
  switchNodeTop({ rootState, state, commit }, { nodes }) {
    if (!nodes) return

    if (!_.isArray(nodes)) {
      nodes = [nodes]
    }

    // 按照树正序排列
    nodes = sortNodeByReverseTree(
      nodes,
      _.get(
        rootState,
        'workspace.game.sceneManager.currentSceneNode.children',
      ) || [],
    )
    // 当前场景节点
    const currentSceneNode = _.get(state, 'sceneManager.currentSceneNode')

    // 获取第一个节点
    const isMulti = nodes.length > 1
    let tempData
    const pickPosition = (node) => {
      const index = node.parent.getChildIndex(node)
      // 到达根节点顶部
      if (
        index === node.parent.children.length - 1 &&
        node.parent.uuid === currentSceneNode.uuid
      ) {
        return {
          target: node.parent,
          index: node.parent.children.length,
        }
      } else if (
        index === node.parent.children.length - 1 &&
        node.parent.uuid !== currentSceneNode.uuid
      ) {
        return {
          target: node.parent.parent,
          index: node.parent.parent.children.length,
        }
      }
      return {
        target: node.parent,
        index: node.parent.children.length,
      }
    }
    if (
      isMulti &&
      !nodes.every((item) => item.parent.uuid === nodes[0].parent.uuid)
    ) {
      // 多选操作且节点不在同一父级
      tempData = {
        nodes,
        targetNode: currentSceneNode,
        targetIndex: currentSceneNode.children.length,
      }
    } else {
      // 选取排序第一位节点
      const position = pickPosition(nodes[0])
      tempData = {
        nodes: nodes,
        targetNode: position.target,
        targetIndex: position.index,
      }
    }
    store.dispatch('workspace/game/switchParent', tempData)
  },

  /**
   * 下移一层
   * @returns
   */
  switchNodeDown({ rootState, state, commit }, { nodes }) {
    if (!nodes) return

    if (!_.isArray(nodes)) {
      nodes = [nodes]
    }

    // 按照树正序排列
    nodes = sortNodeByReverseTree(
      nodes,
      _.get(
        rootState,
        'workspace.game.sceneManager.currentSceneNode.children',
      ) || [],
    )
    // 当前场景节点
    const currentSceneNode = _.get(state, 'sceneManager.currentSceneNode')

    // 获取最后一个节点
    const pickNode = nodes[nodes.length - 1]

    const pickPosition = (node) => {
      const index = node.parent.getChildIndex(node)
      // 到达根节点顶部
      if (index === 0 && node.parent.uuid === currentSceneNode.uuid) {
        return {
          target: node.parent,
          index: 0,
        }
      } else if (index === 0 && node.parent.uuid !== currentSceneNode.uuid) {
        return {
          target: node.parent.parent,
          index: node.parent.parent.getChildIndex(node.parent),
        }
      }
      return {
        target: node.parent,
        index: index - 1,
      }
    }
    const position = pickPosition(pickNode)
    store.dispatch('workspace/game/switchParent', {
      nodes: nodes,
      targetNode: position.target,
      targetIndex: position.index,
    })
  },

  /**
   * 下移到底部
   * @returns
   */
  switchNodeBottom({ rootState, state, commit }, { nodes }) {
    if (!nodes) return

    if (!_.isArray(nodes)) {
      nodes = [nodes]
    }

    // 按照树正序排列
    nodes = sortNodeByReverseTree(
      nodes,
      _.get(
        rootState,
        'workspace.game.sceneManager.currentSceneNode.children',
      ) || [],
    )
    // 当前场景节点
    const currentSceneNode = _.get(state, 'sceneManager.currentSceneNode')
    const isMulti = nodes.length > 1
    let tempData

    const pickPosition = (node) => {
      const index = node.parent.getChildIndex(node)
      // 到达根节点顶部
      if (index === 0 && node.parent.uuid === currentSceneNode.uuid) {
        return {
          target: node.parent,
          index: 0,
        }
      } else if (index === 0 && node.parent.uuid !== currentSceneNode.uuid) {
        return {
          target: node.parent.parent,
          index: 0,
        }
      }
      return {
        target: node.parent,
        index: 0,
      }
    }

    if (
      isMulti &&
      !nodes.every((item) => item.parent.uuid === nodes[0].parent.uuid)
    ) {
      // 多选操作且节点不在同一父级
      tempData = {
        nodes,
        targetNode: currentSceneNode,
        targetIndex: 0,
      }
    } else {
      // 获取最后一个节点
      const pickNode = nodes[nodes.length - 1]
      const position = pickPosition(pickNode)
      tempData = {
        nodes: nodes,
        targetNode: position.target,
        targetIndex: position.index,
      }
    }
    store.dispatch('workspace/game/switchParent', tempData)
  },

  /**
   * 粘贴节点事件
   */
  pasteNodeEvent(
    { rootState, state },
    { originalSceneNode, originalNodes, nodes, sceneNode, record = true },
  ) {
    if (!nodes) return
    if (!_.isArray(nodes)) {
      nodes = [nodes]
    }
    const currentCopyNode = originalNodes[0]
    const currentSceneOfCopiedNode = originalSceneNode
    const copyVpAst = getVpAstFromSceneNode(currentSceneOfCopiedNode)
    const copyNodeAst = getEventOnNode(copyVpAst, currentCopyNode)
    const targetVpAst = getVpAstFromSceneNode(sceneNode)
    const targetNodesAst = {}
    nodes.forEach((node) => {
      targetNodesAst[node.uuid] = getEventOnNode(targetVpAst, node)
    })
    executeAction({
      action: getPasteNodeEventAction({
        newValue: {
          copyScene: currentSceneOfCopiedNode,
          copyNodeAst,
          targetNodes: nodes,
          sceneNode,
        },
        oldValue: { targetNodesAst, sceneNode, targetNodes: nodes },
      }),
      record,
    })
  },

  setImage,
  dragTargetArea,
  addDragBound,
  recoverDragTargetAreaByUuid,
  recoverDragBoundByUuid,
  tweenPositionArea,

  addFilter({ state }, { node, filters }) {
    node = node || state.activeNode
    if (node) {
      const scriptData = editorScript.getScript(node)
      _.each(filters, (value, key) => {
        // 撤销重做时，外观数据未更新，数据未响应式
        scriptData.$data.filters = {
          ...scriptData.$data.filters,
          ...{ [key]: value },
        }
      })
    }
  },

  addBgFilter({ dispatch }, { node, filters }) {
    const configBg = bgScript.getScript(node, false)
    if (configBg) {
      configBg.updateBgFilter(filters)
    } else {
      dispatch('addFilter', { node: this.gamePlay, filters })
    }
  },

  async addFilterToImage(
    { state, dispatch },
    { node, file, filters, padding, record = true },
  ) {
    await executeAction({
      action: {
        execute: async ({ newValue }) => {
          const { node, filters, padding, file } = newValue
          dispatch('addFilter', { node, filters })
          // 图片节点存在特殊逻辑
          let component
          const resourcePath = AssetRoute.GAME_ROOT
          if (isImage(node)) {
            component = await createImageComponentBeforeUpdateImage({
              file,
              node,
              resourcePath,
            })
          } else {
            component = await createImageComponent({
              file,
              resourcePath,
            })
          }
          await updateImageComponent({
            data: component,
            resetImageAfterAdd: false,
            record: false,
            resourcePath,
            nodes: [node],
          })
          // 加滤镜之后，生成新的 bin 文件丢失了 padding 信息，需要重新设置
          // todo：将 padding 信息放到 bin 文件中，而不是后面再设置
          setPaddingToNode(node, padding)
        },
        undo: async ({ oldValue }) => {
          const { node, filters, padding, resourceUuid } = oldValue
          dispatch('addFilter', { node, filters })
          await updateAsset({
            nodeUuid: node.uuid,
            resourceUuid,
          })
          setPaddingToNode(node, padding)
        },
        value: {
          newValue: {
            node,
            filters,
            file,
            padding,
          },
          oldValue: {
            resourceUuid: node.texture.atlas.uuid,
            node,
            filters: editorScript.getScript(node).$data.filters,
            padding,
          },
        },
        async: true,
        methodName: 'addFilterToImageAction',
      },
      record,
    })
  },

  /**
   * 重置节点资源大小
   */
  async resetNativeSize({ state, dispatch }, { node, record = true, reset }) {
    node = node || state.activeNode
    const nativeSize = getNativeSize(node)
    if (nativeSize && nativeSize.width && nativeSize.height) {
      const { width, height } = getResponsiveBox({
        maxWidth: reset ? nativeSize.width : gameIframe.UIRoot.width, // 如果reset为true,则重置为原/裁剪后图片的宽
        maxHeight: reset ? nativeSize.height : gameIframe.UIRoot.height, // 如果reset为true,则重置为原/裁剪后图片的高
        width: nativeSize.width,
        height: nativeSize.height,
      })
      // 如果宽高没变化就不执行重置操作
      if (width === node.width && height === node.height) {
        return
      }
      // 记录修改前后状态值，保证历史回滚正常
      const preNodes = [
        {
          height: node.height,
          width: node.width,
          uuid: node.uuid,
        },
      ]
      const nodesStyle = [
        {
          height,
          width,
          uuid: node.uuid,
        },
      ]
      dispatch('setStyle', {
        nodes: nodesStyle,
        preNodes: preNodes,
        record,
      })
    }
  },

  async setText({ rootState, state }, { value, node, record = true }) {
    node = node || state.activeNode
    node.text = value
    const fontList = await store.dispatch('config/getFontList')
    const assetFontList = rootState.assets.assetFontList
    const fontName = node.font
    await getFont({
      fontName,
      text: getTextByFont(fontName, value),
      fontList: fontList.concat(assetFontList),
      pid: store.state.workspace.pid,
    })
    await store.dispatch('workspace/game/setStyle', {
      nodes: [
        {
          text: value,
          uuid: node.uuid,
        },
      ],
      record,
    })
  },

  async setFont({ rootState, state }, { value, node, record = true }) {
    node = node || state.activeNode
    const fontName = value
    const fontList = await store.dispatch('config/getFontList')
    const assetFontList = rootState.assets.assetFontList
    const newFontName = await getFont({
      fontName,
      text: getTextByFont(fontName, node.text),
      fontList: fontList.concat(assetFontList),
      pid: store.state.workspace.pid,
    })
    await store.dispatch('workspace/game/setStyle', {
      nodes: [
        {
          font: newFontName,
          uuid: node.uuid,
        },
      ],
    })
  },
}
